import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  of,
  skip,
  startWith,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { TestCaseService } from '../../../services/test-case.service';
import { TestRunService } from '../../../services/test-run.service';
import { ReleaseService } from '../../../services/release.service';
import { TestRunEnvironment } from '../../../models/test-run.model';
import { TestCaseHandoverService } from '../../../services/test-case-handover.service';
import { TestCaseLinksService } from '../../../services/test-case-links.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CreateTestCaseLinkRequest } from '../../../models/test-case-link.model';
import { ProjectMemberService } from '../../../services/project-member.service';
import { TestStepService } from '../../../services/test-step.service';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { AccountService } from '../../../account/account.service';
import {
  AutomationStatus,
  TestCaseAutomationFramework,
  SaveFullTestCaseResponse,
  TEST_CASE_PRIORITIES,
  TEST_CASE_STATUSES,
  TEST_CASE_TYPES,
  TEST_TYPES,
  TestCase,
  TestCasePriority,
  TestCaseStatus,
  TestCaseType,
  TestStep,
  TestType,
} from '../../../models/test-case.model';
import { TestCaseFormRaw, toSaveFullRequest } from './test-case-form.mappers';
import { VersionDiffPanelComponent } from '../version-diff-panel/version-diff-panel.component';
import { TestCaseActivityFeedComponent } from '../test-case-activity-feed/test-case-activity-feed.component';
import { TestCaseHandoverChainComponent } from '../test-case-handover-chain/test-case-handover-chain.component';
import { RequirementLinkBlockComponent } from '../requirement-link-block/requirement-link-block.component';
import { TestStepTableComponent } from '../test-step-table/test-step-table.component';
import { ModuleEditDrawerComponent } from '../../module/module-edit-drawer/module-edit-drawer.component';
import { TestCaseAutomationCardComponent } from '../test-case-automation-card/test-case-automation-card.component';
import { TestCaseExecutionStatsComponent } from '../test-case-execution-stats/test-case-execution-stats.component';
import { TestCaseLinksComponent } from '../test-case-links/test-case-links.component';
import { AddLinkModalComponent } from '../add-link-modal/add-link-modal.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { PersonMultiSelectComponent } from '../../../components/person-multi-select/person-multi-select.component';
import { ReviewerService } from '../../../services/reviewer.service';
import { ReviewerDTO } from '../../../models/reviewer.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { TdbOverrideDelta, applyTdbOverride } from '../../../shared/tdb-override-delta';
import { TdbOverride } from '../../../shared/tcdl-ops';
import { createFormDirtyTracker } from '../../../shared/forms/dirty-tracker';
import { createdRunRoute } from '../../../shared/test-run-routes';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { requiredNotBlank } from '../../../shared/forms/required-not-blank.validator';
import { serverMessage } from '../../object-mapping/server-message';
import { RequirementFormDraftStore } from '../../requirements/requirement-form/requirement-form-draft.store';
import { KiQualitaetsCheckComponent } from '../../../components/ki/ki-qualitaets-check/ki-qualitaets-check.component';
import { KiQualitaetAenderung, KiQualitaetZiel } from '../../../models/ki.model';
import { KiMarkerComponent } from '../../../components/ki/ki-marker/ki-marker.component';
import { findRouteParam } from '../../../shared/route-params';

/**
 * Umgebung eines aus dem Formular gestarteten Laufs. Der Schnellstart fragt nicht nach,
 * und STAGING ist die Umgebung, in der geprueft wird.
 */
const MANUAL_RUN_ENVIRONMENT: TestRunEnvironment = 'STAGING';

// Nur beim Link-Typ REQUIREMENT bedeutet 404 eine unbekannte Anforderung (NEX-1493); sonst etwa einen fehlenden Testfall.
function missingRequirementKey(request: CreateTestCaseLinkRequest, err: unknown): string | null {
  return request.type === 'REQUIREMENT' && err instanceof HttpErrorResponse && err.status === 404
    ? request.reference
    : null;
}

function isStaleVersion(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 409 && err.error?.code === 'STALE_VERSION';
}

@Component({
  selector: 'app-test-case-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInput,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    VersionDiffPanelComponent,
    TestCaseActivityFeedComponent,
    TestCaseHandoverChainComponent,
    RequirementLinkBlockComponent,
    TestStepTableComponent,
    ModuleEditDrawerComponent,
    TestCaseAutomationCardComponent,
    TestCaseExecutionStatsComponent,
    TestCaseLinksComponent,
    AddLinkModalComponent,
    SectionTitleComponent,
    PersonSelectComponent,
    PersonMultiSelectComponent,
    Modal,
    KiQualitaetsCheckComponent,
    KiMarkerComponent,
  ],
  templateUrl: './test-case-form.component.html',
  styleUrl: './test-case-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly testRunService = inject(TestRunService);
  private readonly releaseService = inject(ReleaseService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);
  private readonly handoverService = inject(TestCaseHandoverService);
  private readonly linksService = inject(TestCaseLinksService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly testStepService = inject(TestStepService);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly translateService = inject(TranslateService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly reviewerService = inject(ReviewerService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly accountService = inject(AccountService);
  private readonly draftStore = inject(RequirementFormDraftStore);

  private get returnToRequirementId(): string | null {
    const qp = this.route.snapshot.queryParamMap;
    return qp.get('from') === 'anforderung' ? qp.get('anfId') : null;
  }

  readonly isReadOnly = this.projectRoleService.isProjectReader;

  readonly projectId: string;
  /** Ein manueller Lauf liess sich nicht anlegen (NEX-1027). */
  readonly manualRunError = signal(false);
  readonly startingManualRun = signal(false);

  /**
   * Legt einen manuellen Lauf ueber diesen Testfall an und springt hinein.
   *
   * <p>Haengt am Ausfuehren-Dialog der Automatisierungs-Karte: Wer dort „Manuell" waehlt,
   * will den Testfall selbst durchgehen statt ihn einem Runner zu geben — ein Lauf ueber
   * genau diesen Fall, danach direkt in den Lauf.
   *
   * <p>Ohne gespeicherten Testfall gibt es nichts auszufuehren; im Anlegen-Modus passiert
   * deshalb nichts. Der Lauf gehoert zum aktuellen Release in Abnahme (NEX-1450); hat
   * das Projekt keines, laesst sich kein Lauf anlegen.
   */
  startManualRun(): void {
    const testCaseId = this.testCaseId;
    if (!testCaseId || this.startingManualRun()) {
      return;
    }
    this.manualRunError.set(false);
    this.startingManualRun.set(true);
    this.releaseService
      .currentRelease(this.projectId)
      .pipe(
        switchMap((release) =>
          release
            ? this.testRunService.create(this.projectId, {
                kind: 'MANUAL',
                sourceType: 'CASES',
                releaseId: release.id,
                caseIds: [testCaseId],
                environment: MANUAL_RUN_ENVIRONMENT,
              })
            : throwError(() => new Error('Projekt ohne aktuelles Release')),
        ),
        finalize(() => this.startingManualRun.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (created) => {
          const run = created[0];
          if (!run) {
            this.manualRunError.set(true);
            return;
          }
          this.router.navigate(createdRunRoute(this.projectId, run));
        },
        error: () => this.manualRunError.set(true),
      });
  }

  get testCaseId(): string | null {
    return this.findParam('id');
  }
  get isEdit(): boolean {
    return this.testCaseId !== null;
  }
  private savedTestCaseId: string | null;

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorText = signal<string | null>(null);
  readonly missingRequirementKey = signal<string | null>(null);
  readonly testCase = signal<TestCase | null>(null);
  /** Lokaler Draft-Zustand aller Testschritte (NEX-599 Buffered-Save). */
  readonly draftSteps = signal<TestStep[]>([]);
  /** Erst rendern, wenn die Ausgangs-Schritte im draft-State stehen — sonst hydriert der Table mit []. */
  readonly stepsReady = signal(false);

  /** NEX-686: aktuell im Right-Drawer bearbeitetes eingebundenes Modul; null = Drawer zu. */
  readonly editingModuleId = signal<string | null>(null);
  /**
   * NEX-1052: Step-ID der MODULE_REF-Zeile, aus der der Drawer geoeffnet wurde. Nur
   * wenn gesetzt, blendet der Drawer die Testfall-spezifische TDB-Sektion ein und
   * das „Uebernehmen" propagiert das Override auf genau diese Zeile.
   */
  readonly editingContextStepId = signal<string | null>(null);
  readonly editingContextStep = computed<TestStep | null>(() => {
    const stepId = this.editingContextStepId();
    if (!stepId) return null;
    return this.draftSteps().find((s) => s.id === stepId) ?? null;
  });
  readonly editingContextOverride = computed<TdbOverride | null>(() => {
    const stepId = this.editingContextStepId();
    if (!stepId) return null;
    return this.stepTable()?.overrideDraftFor(stepId) ?? null;
  });
  readonly linkedRequirementId = signal<string | null>(null);
  readonly linkedStoryKey = signal<string | null>(null);
  readonly pendingLinkDrafts = signal<CreateTestCaseLinkRequest[]>([]);
  readonly pendingLinkDeletions = signal<string[]>([]);
  /** Child-Referenz fuer Reconciliation nach Partial-Save (NEX-714 Review Runde 2). */
  private readonly linksBlock = viewChild<TestCaseLinksComponent>('linksBlock');
  private readonly handoverBlock = viewChild<TestCaseHandoverChainComponent>('handoverBlock');
  /** Der Auftrag lebt in der Karte, damit sein Status dort erscheint; die Leiste stößt ihn nur an. */
  protected readonly automationCard = viewChild<TestCaseAutomationCardComponent>('automationCard');
  /**
   * NEX-1052 Follow-up: das Step-Table haelt seinen eigenen `steps`-Signal-Zustand,
   * der beim `ngOnInit` einmal aus `initialSteps` geseedet wird. Damit ein vom
   * Drawer emittiertes TDB-Delta die Chip-Anzeige sofort aktualisiert, patchen wir
   * hier zusaetzlich direkt in die Tabelle.
   */
  private readonly stepTable = viewChild<TestStepTableComponent>('stepTable');
  readonly pendingHandoverDraft = signal<string | null>(null);
  readonly handoverError = signal<string | null>(null);
  readonly currentUserId = signal<string | null>(null);
  readonly showLinkModal = signal(false);
  /**
   * NEX-1051: alle Testdatenblaetter des Projekts. Wird einmal beim Formular-Load
   * gefetched und an das Step-Table gereicht, damit dort die Modul-TDB-Chips und der
   * Override-Block rendern koennen.
   */
  readonly availableSheets = signal<readonly Testdatenblatt[]>([]);
  readonly refreshing = signal(false);
  readonly refreshError = signal(false);

  /**
   * App-Modal-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). Ein Modal bedient beide
   * Flows — Verlassen-Guard und Refresh-aus-Anforderung — über dynamische Texte.
   */
  readonly showDeactivateConfirm = signal(false);
  readonly deactivateMessage = signal('testCases.form.unsavedChanges');
  readonly deactivateConfirmLabel = signal('common.unsavedChanges.leave');
  readonly deactivateCancelLabel = signal('common.unsavedChanges.stay');
  private readonly deactivateConfirm$ = new Subject<boolean>();

  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));
  readonly loadedReviewerIds = signal<string[]>([]);

  readonly typeOptions = TEST_CASE_TYPES;
  readonly testTypeOptions = TEST_TYPES;
  readonly statusOptions = TEST_CASE_STATUSES;
  readonly priorityOptions = TEST_CASE_PRIORITIES;

  readonly jiraTicketFields: readonly {
    controlName: 'storyJiraKey' | 'bugJiraKey';
    labelKey: string;
    placeholderKey: string;
  }[] = [
    {
      controlName: 'storyJiraKey',
      labelKey: 'testCases.form.properties.storyTicket',
      placeholderKey: 'testCases.form.properties.storyTicketPlaceholder',
    },
    {
      controlName: 'bugJiraKey',
      labelKey: 'testCases.form.properties.bugTicket',
      placeholderKey: 'testCases.form.properties.bugTicketPlaceholder',
    },
  ];

  readonly fieldLengths = FIELD_LENGTHS;

  readonly form = this.fb.group({
    title: ['', [requiredNotBlank, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
    description: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    testGoal: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    preconditions: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    requirementId: [''],
    storyJiraKey: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    bugJiraKey: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    notes: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    type: this.fb.control<TestCaseType>('FUNCTIONAL', Validators.required),
    testType: this.fb.control<TestType>('SYSTEM', Validators.required),
    priority: this.fb.control<TestCasePriority>('MEDIUM'),
    status: this.fb.control<TestCaseStatus>('DRAFT', Validators.required),
    assigneeUserId: ['', Validators.required],
    reviewerIds: this.fb.control<string[]>([]),
    tags: this.fb.control<string[]>([]),
    automationStatus: this.fb.control<AutomationStatus>('MANUAL'),
    automationFramework: this.fb.control<TestCaseAutomationFramework | null>(null),
    automationLocator: this.fb.control<string>(''),
  });

  readonly basicsSectionKeys: { title: string; subtitle: string };

  /** Rohwerte auch im gesperrten Formular (PROJECT_READER): der KI-Qualitätscheck prüft den aktuellen Stand. */
  private readonly formWerte = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });
  readonly kiTitel = computed(() => this.formWerte().title);
  readonly kiVorbedingung = computed(() => this.formWerte().preconditions);

  /** Absprung „Befunde ansehen“ aus dem Postfach: gespeichertes Ergebnis zeigen statt neu anfragen. */
  readonly kiAnfrageId = toSignal(this.route.queryParamMap.pipe(map((params) => params.get('kiAnfrage'))), {
    initialValue: null,
  });
  private readonly kiCheck = viewChild(KiQualitaetsCheckComponent);
  private readonly vorbedingungInput = viewChild<ElementRef<HTMLInputElement>>('vorbedingungInput');
  private readonly kiOffeneBefunde = computed(() => this.kiCheck()?.offeneBefunde() ?? []);
  private readonly kiHervorgehoben = computed(() => this.kiCheck()?.hervorgehoben() ?? null);
  readonly kiBefundSchrittIds = computed(() =>
    this.kiOffeneBefunde().flatMap((ziel) => (ziel.feld !== 'VORBEDINGUNG' && ziel.schrittId ? [ziel.schrittId] : [])),
  );
  readonly kiVorbedingungBefund = computed(() => this.kiOffeneBefunde().some((ziel) => ziel.feld === 'VORBEDINGUNG'));
  readonly kiHervorgehobenSchrittId = computed(() => {
    const ziel = this.kiHervorgehoben();
    return ziel && ziel.feld !== 'VORBEDINGUNG' ? ziel.schrittId : null;
  });
  readonly kiVorbedingungHervorgehoben = computed(() => this.kiHervorgehoben()?.feld === 'VORBEDINGUNG');
  readonly kiVorbedingungGeaendert = signal(false);

  /**
   * Value-based dirty tracking (NEX-603): the form value plus the buffered step
   * draft form the tracked content. Reverting a change back to the loaded state
   * clears the dirty pill and re-disables save (NEX-535/NEX-599). The tracker's
   * result is mirrored onto {@code form.dirty} so template, guard and save-bar
   * keep reading the single Angular flag.
   */
  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({
      steps: this.stepsSignature(this.draftSteps()),
      links: this.linksSignature(this.pendingLinkDrafts()),
      linkDeletions: [...this.pendingLinkDeletions()].sort().join(','),
      handover: this.pendingHandoverDraft() ?? '',
    }),
  });

  private readonly requiredFieldLabelKeys: Record<string, string> = {
    title: 'testCases.form.title',
    type: 'testCases.form.properties.type',
    testType: 'testCases.form.properties.testType',
    status: 'testCases.form.properties.status',
    assigneeUserId: 'testCases.form.properties.assignee',
  };

  saveDisabled(): boolean {
    return (
      this.saving() ||
      this.form.invalid ||
      (this.isEdit && !this.form.dirty) ||
      this.incompleteConditionSteps().length > 0
    );
  }

  incompleteConditionSteps(): number[] {
    return this.stepTable()?.incompleteConditionStepNumbers() ?? [];
  }

  saveBlockedDescribedBy(missingRequired: boolean, incompleteCondition: boolean): string | null {
    const ids = [
      missingRequired ? 'tc-save-blocked-reason' : null,
      incompleteCondition ? 'tc-save-blocked-condition' : null,
    ].filter(Boolean);
    return ids.length ? ids.join(' ') : null;
  }

  /** Im Anlegen-Modus bleibt der Hinweis aus: dort ist ein leeres Formular der erwartete Ausgangszustand. */
  missingRequiredFieldNames(): string[] {
    if (!this.isEdit || this.loading() || this.loadError()) return [];
    return Object.entries(this.requiredFieldLabelKeys)
      .filter(([controlName]) => this.form.get(controlName)?.hasError('required'))
      .map(([, labelKey]) => this.translateService.instant(labelKey) as string);
  }

  private markMissingRequiredFieldsAsTouched(): void {
    for (const controlName of Object.keys(this.requiredFieldLabelKeys)) {
      const control = this.form.get(controlName);
      if (control?.hasError('required')) {
        control.markAsTouched();
      }
    }
  }

  /**
   * Übernimmt den aktuellen Draft-Schritt-Zustand vom Table (NEX-599 Buffered-Save)
   * und synct dirty/pristine gegen die Content-Signature nach Load. Toggle-on-off
   * eines Bibliotheks-Schritts revertiert damit den Dirty-State (NEX-535, NEX-599).
   */
  onStepsChanged(steps?: TestStep[]): void {
    if (steps !== undefined) {
      this.draftSteps.set(steps);
    }
    this.syncDirtyFromContent();
  }

  onDraftLinksChanged(drafts: CreateTestCaseLinkRequest[]): void {
    this.pendingLinkDrafts.set(drafts);
    this.syncDirtyFromContent();
  }

  onPendingDeletionsChanged(ids: string[]): void {
    this.pendingLinkDeletions.set(ids);
    this.syncDirtyFromContent();
  }

  private linksSignature(list: CreateTestCaseLinkRequest[]): string {
    return JSON.stringify(list.map((l) => ({ type: l.type, ref: l.reference, label: l.displayLabel ?? null })));
  }

  onHandoverChainChanged(): void {}

  onHandoverDraftChange(userId: string | null): void {
    this.pendingHandoverDraft.set(userId);
    // Auch beim Verwerfen (userId === null) den letzten Fehler räumen — sonst bleibt
    // nach einem 409 die Meldung stehen, obwohl der Nutzer den Draft zurücknimmt.
    this.handoverError.set(null);
    this.syncDirtyFromContent();
  }

  private snapshotInitialState(): void {
    this.dirtyTracker.reset();
    this.form.markAsPristine();
  }

  /**
   * Mirrors value-based dirtiness onto {@code form.dirty}, so template, save-bar
   * and the can-deactivate guard keep reading the single Angular flag while the
   * comparison itself is value-based (reverting a change clears it — NEX-603).
   */
  private syncDirtyFromContent(): void {
    if (this.dirtyTracker.isDirtyNow()) {
      this.form.markAsDirty();
    } else {
      this.form.markAsPristine();
    }
  }

  private stepsSignature(steps: TestStep[]): string {
    return JSON.stringify(
      steps.map((s) => ({
        id: s.id,
        kind: s.kind,
        action: s.action ?? null,
        expectedResult: s.expectedResult ?? null,
        notes: s.notes ?? null,
        tcdl: s.tcdl ?? null,
        status: s.status,
        title: s.title ?? null,
        libraryStepId: s.libraryStepId ?? null,
        calledModuleId: s.calledModuleId ?? null,
        // NEX-1051: TDB-Override am Modulaufruf gehoert zum Dirty-Vergleich.
        dataSheetId: s.dataSheetId ?? null,
        dataSheetFilters: s.dataSheetFilters ?? null,
        stepCondition: s.stepCondition ?? null,
      })),
    );
  }

  constructor() {
    warnBeforeUnload(() => this.form.dirty && !this.saving());
    this.projectId = this.requireParam('projectId');
    this.savedTestCaseId = this.testCaseId;
    this.basicsSectionKeys = this.isEdit
      ? {
          title: 'testCases.form.sections.requirementContext.title',
          subtitle: 'testCases.form.sections.requirementContext.subtitle',
        }
      : {
          title: 'testCases.form.sections.basics.title',
          subtitle: 'testCases.form.sections.basics.subtitle',
        };

    effect(() => {
      if (this.isReadOnly()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnInit(): void {
    this.form.controls.requirementId.valueChanges
      .pipe(startWith(this.form.controls.requirementId.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => this.linkedRequirementId.set(id.trim() || null));

    this.form.controls.storyJiraKey.valueChanges
      .pipe(startWith(this.form.controls.storyJiraKey.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((key) => this.linkedStoryKey.set(key.trim() || null));

    this.loadUsers();
    this.loadAvailableSheets();

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncDirtyFromContent());

    if (this.isEdit && this.testCaseId) {
      this.loadTestCase(this.testCaseId);
    } else {
      this.stepsReady.set(true);
      const requirementId = this.route.snapshot.queryParamMap.get('requirementId');
      if (requirementId) {
        this.preFillFromRequirement(requirementId);
      } else {
        this.snapshotInitialState();
      }
    }

    // Angular's default RouteReuseStrategy hält die Component-Instanz zwischen
    // /new und /:id/edit am Leben — ngOnInit läuft dann nicht erneut. Wir hören
    // auf paramMap/queryParamMap, um Route-Wechsel innerhalb derselben Instanz
    // zu erkennen. skip(1) überspringt die initiale synchrone Emission, die
    // bereits durch die Snapshot-basierte Initialisierung oben abgedeckt ist.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        skip(1),
        map(([pm, qp]) => ({ id: pm.get('id'), requirementId: qp.get('requirementId') })),
        distinctUntilChanged((a, b) => a.id === b.id && a.requirementId === b.requirementId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ id, requirementId }) => {
        if (id) {
          this.savedTestCaseId = id;
          this.loadTestCase(id);
          return;
        }
        this.stepsReady.set(true);
        this.resetCreateState();
        if (requirementId) {
          this.preFillFromRequirement(requirementId);
        } else {
          this.snapshotInitialState();
        }
      });
  }

  private resetCreateState(): void {
    this.savedTestCaseId = null;
    this.saving.set(false);
    this.saveError.set(false);
    this.saveErrorText.set(null);
    this.form.reset({
      title: '',
      description: '',
      testGoal: '',
      preconditions: '',
      requirementId: '',
      storyJiraKey: '',
      bugJiraKey: '',
      notes: '',
      type: 'FUNCTIONAL',
      testType: 'SYSTEM',
      priority: 'MEDIUM',
      status: 'DRAFT',
      assigneeUserId: '',
      reviewerIds: [],
      tags: [],
      automationStatus: 'MANUAL',
      automationFramework: null,
      automationLocator: '',
    });
    this.draftSteps.set([]);
    this.pendingLinkDrafts.set([]);
    this.pendingLinkDeletions.set([]);
    this.pendingHandoverDraft.set(null);
    this.loadedReviewerIds.set([]);
  }

  addTag(event: MatChipInputEvent): void {
    if (this.isReadOnly()) return;
    const value = (event.value ?? '').trim();
    const tagsControl = this.form.controls.tags;
    if (value && !tagsControl.value.includes(value)) {
      tagsControl.setValue([...tagsControl.value, value]);
      tagsControl.markAsDirty();
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    if (this.isReadOnly()) return;
    const tagsControl = this.form.controls.tags;
    tagsControl.setValue(tagsControl.value.filter((t) => t !== tag));
    tagsControl.markAsDirty();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving() || this.incompleteConditionSteps().length > 0) {
      return;
    }
    this.saving.set(true);
    this.saveError.set(false);
    this.saveErrorText.set(null);
    this.missingRequirementKey.set(null);
    const raw = this.form.getRawValue();
    const ids = {
      requirementId: raw.requirementId.trim(),
      storyJiraKey: raw.storyJiraKey.trim(),
      bugJiraKey: raw.bugJiraKey.trim(),
    };
    const request = toSaveFullRequest(raw as TestCaseFormRaw, ids, this.draftSteps());

    const editingId = this.savedTestCaseId;
    const request$ = editingId
      ? this.testCaseService.saveFull(this.projectId, editingId, request)
      : this.testCaseService.createFull(this.projectId, request);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.applyClientIdMapping(response);
        const drafts = this.pendingLinkDrafts();
        const deletions = this.pendingLinkDeletions();
        const persistedId = response.testCase?.id ?? this.savedTestCaseId;
        this.savedTestCaseId = persistedId;
        if ((drafts.length === 0 && deletions.length === 0) || !persistedId) {
          this.flushReviewersThenFinish(persistedId);
          return;
        }
        // NEX-714-Review (fboudouhiST): pro Request eigenen catchError-Wrapper
        // statt gemeinsamem forkJoin-Error, damit erfolgreich abgeschlossene
        // Operationen nicht bei einem Teilausfall des naechsten Save-Versuchs
        // dupliziert werden.
        const draftOps$ = drafts.map((request) =>
          this.linksService.create(this.projectId, persistedId, request).pipe(
            map(() => ({ ok: true as const, kind: 'draft' as const, request })),
            catchError((err: unknown) =>
              of({
                ok: false as const,
                kind: 'draft' as const,
                request,
                missingKey: missingRequirementKey(request, err),
              }),
            ),
          ),
        );
        const deletionOps$ = deletions.map((linkId) =>
          this.linksService.delete(this.projectId, persistedId, linkId).pipe(
            map(() => ({ ok: true as const, kind: 'deletion' as const, linkId })),
            catchError(() => of({ ok: false as const, kind: 'deletion' as const, linkId })),
          ),
        );
        forkJoin([...draftOps$, ...deletionOps$])
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((results) => {
            const failedDrafts: CreateTestCaseLinkRequest[] = [];
            const failedDeletions: string[] = [];
            const successfulDrafts: CreateTestCaseLinkRequest[] = [];
            const successfulDeletions: string[] = [];
            let missingKey: string | null = null;
            for (const r of results) {
              if (r.kind === 'draft') {
                (r.ok ? successfulDrafts : failedDrafts).push(r.request);
                if (!r.ok) {
                  missingKey ??= r.missingKey;
                }
              } else {
                (r.ok ? successfulDeletions : failedDeletions).push(r.linkId);
              }
            }
            if (failedDrafts.length === 0 && failedDeletions.length === 0) {
              this.linksBlock()?.resetPending();
              this.pendingLinkDrafts.set([]);
              this.pendingLinkDeletions.set([]);
              this.flushReviewersThenFinish(persistedId);
              return;
            }
            // Child-State reconcilen: erfolgreiche Ops raus, sonst emittiert
            // der Child beim naechsten Add wieder die volle Draft-Liste und
            // ueberschreibt pendingLinkDrafts mit den bereits angelegten
            // Requests -> doppelte Links beim naechsten Save (NEX-714 R2).
            this.linksBlock()?.applyPartialSuccess(successfulDrafts, successfulDeletions);
            this.pendingLinkDrafts.set(failedDrafts);
            this.pendingLinkDeletions.set(failedDeletions);
            this.saving.set(false);
            this.saveError.set(true);
            this.missingRequirementKey.set(missingKey);
          });
      },
      // Draft-State bleibt intakt: draftSteps + Form-Werte werden nicht angefasst.
      error: (err: unknown) => {
        this.saveErrorText.set(isStaleVersion(err) ? null : serverMessage(err));
        this.saving.set(false);
        this.saveError.set(true);
      },
    });
  }

  private flushReviewersThenFinish(persistedId: string | null): void {
    if (!persistedId) {
      this.flushHandoverThenFinish(persistedId);
      return;
    }
    const current = this.form.controls.reviewerIds.value;
    const baseline = this.loadedReviewerIds();
    const added = current.filter((id) => !baseline.includes(id));
    const removed = baseline.filter((id) => !current.includes(id));
    if (added.length === 0 && removed.length === 0) {
      this.flushHandoverThenFinish(persistedId);
      return;
    }
    const ops: Observable<boolean>[] = [
      ...added.map((id) =>
        this.reviewerService.add(this.projectId, 'test-cases', persistedId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
      ...removed.map((id) =>
        this.reviewerService.remove(this.projectId, 'test-cases', persistedId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
    ];
    forkJoin(ops)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        if (results.every((ok) => ok)) {
          this.loadedReviewerIds.set(current);
          this.flushHandoverThenFinish(persistedId);
        } else {
          this.saving.set(false);
          this.saveError.set(true);
        }
      });
  }

  private flushHandoverThenFinish(persistedId: string | null): void {
    const pending = this.pendingHandoverDraft();
    if (!persistedId || !pending) {
      this.finishSaveSuccess();
      return;
    }
    this.handoverService
      .handOff(this.projectId, persistedId, pending)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.pendingHandoverDraft.set(null);
          this.handoverError.set(null);
          this.handoverBlock()?.resetPending();
          this.handoverBlock()?.reloadChain();
          this.handoverBlock()?.refreshInboxBadge();
          this.finishSaveSuccess();
        },
        error: (err: HttpErrorResponse) => {
          const code = err?.error?.code;
          const key =
            code === 'USER_ALREADY_HANDED_OFF'
              ? 'testCases.form.handover.errors.alreadyHandedOff'
              : code === 'SELF_HANDOVER_NOT_ALLOWED'
                ? 'testCases.form.handover.errors.selfHandoff'
                : 'testCases.form.handover.errors.persistFailed';
          this.handoverError.set(this.translateService.instant(key));
          this.saving.set(false);
          this.saveError.set(true);
        },
      });
  }

  private finishSaveSuccess(): void {
    this.saving.set(false);
    this.pendingLinkDrafts.set([]);
    this.pendingLinkDeletions.set([]);
    this.pendingHandoverDraft.set(null);
    this.handoverError.set(null);
    this.linksBlock()?.resetPending();
    this.handoverBlock()?.resetPending();
    this.form.markAsPristine();
    if (this.returnToRequirementId && this.savedTestCaseId) {
      this.draftStore.rememberCreatedTestCaseId(this.returnToRequirementId, this.savedTestCaseId);
    }
    this.navigateToList();
  }

  private applyClientIdMapping(response: SaveFullTestCaseResponse): void {
    const mapping = response.clientIdMapping;
    if (!mapping || Object.keys(mapping).length === 0) {
      return;
    }
    this.draftSteps.update((list) => list.map((s) => (mapping[s.id] ? { ...s, id: mapping[s.id] } : s)));
  }

  onCancel(): void {
    this.navigateToList();
  }

  /**
   * NEX-644: Sprung aus einer MODULE_REF-Zeile zum eingebundenen Modul. Der Wechsel
   * verlässt den Testfall-Editor, daher greift der CanDeactivate-Guard für die
   * Dirty-Bestätigung — hier genügt die Navigation.
   */
  onOpenModule(moduleId: string): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', moduleId, 'edit']);
  }

  /**
   * NEX-686: Bearbeiten eines eingebundenen Moduls im Right-Drawer, ohne den Testfall zu
   * verlassen. Die Modul-Zeilen spiegeln die Änderung nach dem Save live (NEX-691 Store).
   */
  onEditModule(event: { moduleId: string; stepId: string | null }): void {
    // Kontext-Step ZUERST setzen, damit das computed `editingContextStep` bereits
    // aktualisiert ist, wenn der Drawer-Effect wegen moduleId-Wechsel feuert.
    this.editingContextStepId.set(event.stepId);
    this.editingModuleId.set(event.moduleId);
  }

  onModuleDrawerClosed(): void {
    this.editingModuleId.set(null);
    this.editingContextStepId.set(null);
  }

  /**
   * NEX-1052: Uebernahme des im Drawer bearbeiteten TDB-Overrides. Der Drawer
   * emittiert den vollstaendigen Delta (dataSheetId + parsed Filter + Bedingung),
   * wir patchen den betreffenden Testfall-Step und triggern das Dirty-Tracking.
   *
   * <p>Das Override lebt bewusst im Draft und persistiert erst beim regulaeren
   * Save des Outer-Formulars — der `stepsSignature`-Fix macht den Speichern-
   * Knopf dafuer klickbar. Ein automatischer Save beim Drawer-`saved`-Event
   * wurde bewusst nicht verdrahtet (sgraetz-Review NEX-1051: `finishSaveSuccess`
   * navigiert bedingungslos zur Liste, im Anlege-Modus faellt der `dirty`-Guard
   * weg — beide Effekte kaepern den Nutzer aus dem Editor).
   */
  onTdbOverrideApplied(delta: TdbOverrideDelta): void {
    this.draftSteps.set(applyTdbOverride(this.draftSteps(), delta));
    // NEX-1052 Follow-up: gleiche Aenderung im Step-Table live nachziehen, damit der
    // TDB-Chip in der Modul-Zeile sofort auf „eingeschraenkt" umspringt.
    const { stepId, ...patch } = delta;
    this.stepTable()?.patchStepOverride(stepId, patch);
    this.syncDirtyFromContent();
  }

  /** „Verbessern“ im KI-Qualitätscheck; die Änderung bleibt ungespeichert wie jede Eingabe. */
  onKiVerbessern(aenderung: KiQualitaetAenderung): void {
    if (this.isReadOnly()) return;
    if (aenderung.feld === 'VORBEDINGUNG') {
      this.form.controls.preconditions.setValue(aenderung.wert);
      this.kiVorbedingungGeaendert.set(true);
      return;
    }
    this.stepTable()?.uebernehmeKiVorschlag(aenderung.schrittId, aenderung.feld, aenderung.wert);
  }

  onKiHervorheben(ziel: KiQualitaetZiel): void {
    if (ziel.feld === 'VORBEDINGUNG') {
      this.vorbedingungInput()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (ziel.schrittId) {
      this.stepTable()?.zeigeSchritt(ziel.schrittId);
    }
  }

  confirmDeactivate(): Observable<boolean> {
    return this.openConfirm({
      message: 'testCases.form.unsavedChanges',
      confirmLabel: 'common.unsavedChanges.leave',
      cancelLabel: 'common.unsavedChanges.stay',
    });
  }

  /**
   * Zeigt die geteilte App-Modal mit den übergebenen i18n-Keys und liefert die
   * User-Entscheidung als einmaliges Observable (NEX-814).
   */
  private openConfirm(config: { message: string; confirmLabel: string; cancelLabel: string }): Observable<boolean> {
    this.deactivateMessage.set(config.message);
    this.deactivateConfirmLabel.set(config.confirmLabel);
    this.deactivateCancelLabel.set(config.cancelLabel);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  refreshFromRequirement(): void {
    if (!this.isEdit || !this.testCaseId || !this.linkedStoryKey()) {
      return;
    }
    // Bei ungespeicherten Eingaben warnen, dass der Refresh Titel/Beschreibung
    // im Formular ersetzt — sonst gingen Edits unbemerkt verloren.
    const message = this.form.dirty ? 'testCases.form.source.confirmDirty' : 'testCases.form.source.confirm';
    this.openConfirm({
      message,
      confirmLabel: 'testCases.form.source.confirmButton',
      cancelLabel: 'common.unsavedChanges.stay',
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.doRefreshFromRequirement();
        }
      });
  }

  private doRefreshFromRequirement(): void {
    if (!this.testCaseId) {
      return;
    }
    this.refreshing.set(true);
    this.refreshError.set(false);
    this.testCaseService
      .refreshFromRequirement(this.projectId, this.testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.testCase.set(updated);
          this.form.patchValue({
            title: updated.title,
            description: updated.description ?? '',
          });
          this.form.markAsPristine();
          this.snapshotInitialState();
          this.refreshing.set(false);
        },
        error: () => {
          this.refreshing.set(false);
          this.refreshError.set(true);
        },
      });
  }

  /**
   * NEX-1051: laedt alle Testdatenblaetter des Projekts. Ein Fehler wird still
   * geschluckt — ohne Blaetter zeigt das Step-Table keine Chips, blockiert aber
   * das Formular nicht. Drawer-Aenderungen kommen nicht hierueber zurueck, sondern
   * als `sheetsChanged`-Snapshot direkt in `availableSheets`.
   */
  loadAvailableSheets(): void {
    this.testdatenblattService
      .list(this.projectId)
      .pipe(
        catchError(() => of([] as Testdatenblatt[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sheets) => this.availableSheets.set(sheets));
  }

  private loadUsers(): void {
    // NEX-822: Projekt-Mitglieder statt des TENANT_ADMIN-only Customers/Users-Endpunkts —
    // ein Projekt-Lead darf diesen Endpunkt aufrufen und bekommt so Reviewer-Kandidaten.
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('testCases.form.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => {
        this.users.set(members);
        this.loadCurrentUserId();
        if (!this.isEdit) {
          this.applyAssigneeDefault();
        }
      });
  }

  private loadCurrentUserId(): void {
    if (this.currentUserId() !== null) return;
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (profile) this.currentUserId.set(profile.id);
      });
  }

  /**
   * NEX-824: prefills the assignee with the current user when creating a new test
   * case. Runs after the project members are loaded so `personOptions()` already
   * reflects the PENDING/inactive filtering from NEX-822 — absent from those
   * options, the field stays empty and required validation forces an explicit pick.
   */
  private applyAssigneeDefault(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (!profile) return;
        this.currentUserId.set(profile.id);
        prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, profile.id, this.personOptions());
        this.snapshotInitialState();
      });
  }

  private loadTestCase(id: string): void {
    this.kiVorbedingungGeaendert.set(false);
    this.loading.set(true);
    forkJoin({
      tc: this.testCaseService.getById(this.projectId, id),
      reviewers: this.reviewerService
        .list(this.projectId, 'test-cases', id)
        .pipe(catchError(() => of([] as ReviewerDTO[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tc, reviewers }) => {
          this.testCase.set(tc);
          const reviewerIds = reviewers.map((r) => r.reviewerUserId);
          this.loadedReviewerIds.set(reviewerIds);
          this.form.patchValue({
            title: tc.title,
            description: tc.description ?? '',
            testGoal: tc.testGoal ?? '',
            preconditions: tc.preconditions ?? '',
            requirementId: tc.requirementId ?? '',
            storyJiraKey: tc.storyJiraKey ?? '',
            bugJiraKey: tc.bugJiraKey ?? '',
            notes: tc.notes ?? '',
            type: tc.type ?? 'FUNCTIONAL',
            testType: tc.testType ?? 'SYSTEM',
            priority: tc.priority ?? 'MEDIUM',
            status: tc.status ?? 'DRAFT',
            assigneeUserId: tc.assigneeUserId ?? '',
            reviewerIds,
            tags: tc.tags ?? [],
            automationStatus: tc.automationStatus ?? 'MANUAL',
            automationFramework: tc.automationFramework ?? null,
            automationLocator: tc.automationLocator ?? '',
          });
          this.form.markAsPristine();
          this.markMissingRequiredFieldsAsTouched();
          this.loading.set(false);
          this.loadDraftSteps(id);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadDraftSteps(testCaseId: string): void {
    // Kein Silent-Fallback auf []: sonst würde ein Save den bestehenden BE-Zustand
    // per PUT /full leer überschreiben (Vollersatz-Semantik).
    this.testStepService
      .list(this.projectId, testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.draftSteps.set(steps);
          this.stepsReady.set(true);
          this.snapshotInitialState();
        },
        error: () => {
          this.loadError.set(true);
        },
      });
  }

  private preFillFromRequirement(requirementId: string): void {
    this.form.patchValue({ requirementId });
    this.form.markAsPristine();
    this.snapshotInitialState();
  }

  private navigateToList(): void {
    if (this.returnToRequirementId) {
      this.router.navigate(['/projects', this.projectId, 'requirements', this.returnToRequirementId, 'edit'], {
        queryParams: { from: 'anforderung', anfId: this.returnToRequirementId },
      });
      return;
    }
    this.router.navigate(['/projects', this.projectId, 'test-cases']);
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
